<template>
  <div class="flex flex-col gap-y-4 relative">
    <h1
      class="heading-md max-lg:px-4"
      v-text="`Welcome, ${user?.firstname} ${user?.lastname}`"
    />

    <LoadingIndicator v-if="isLoading" />

    <BaseAccordion
      v-if="incompleteRegistrations.length > 0"
      :default-open="true"
      button-text="Incomplete registrations"
    >
      <template
        v-for="(item, index) in incompleteRegistrations"
        :key="`incomplete-registrations-${index}`"
      >
        <WebinarCard
          v-if="item.course.type === 'webinar'"
          :registration="item.registration"
          :webinar="item.course"
          status="incomplete"
          @confirm-delete="confirmDelete"
        />
        <CourseCard
          v-else
          :registration="item.registration"
          :course="item.course"
          status="incomplete"
          @confirm-delete="confirmDelete"
        />
      </template>
    </BaseAccordion>

    <BaseAccordion
      button-text="Current & Upcoming Training"
      :default-open="true"
    >
      <template v-if="enrolledRegistrations.length > 0">
        <template
          v-for="(item, index) in enrolledRegistrations"
          :key="`enrolled-registrations-${index}`"
        >
          <WebinarCard
            v-if="item.course.type === 'webinar'"
            :registration="item.registration"
            :webinar="item.course"
            status="current"
          />
          <CourseCard
            v-else
            :registration="item.registration"
            :course="item.course"
            :status="item.registration.status === 'enrollment_pending' ? 'enrollment_pending' : 'current'"
          />
        </template>
      </template>
      <EmptyCurrentUpcomingTraining v-else />
    </BaseAccordion>

    <BaseAccordion
      v-if="completedRegistrations.length > 0"
      button-text="Completed Training"
    >
      <template
        v-for="(item, index) in completedRegistrations"
        :key="`completed-registrations-${index}`"
      >
        <WebinarCard
          v-if="item.course.type === 'webinar'"
          :registration="item.registration"
          :webinar="item.course"
          status="completed"
        />
        <CourseCard
          v-else
          :registration="item.registration"
          :course="item.course"
          status="completed"
        />
      </template>
    </BaseAccordion>

    <BaseAccordion
      v-if="alumResources.length"
      button-text="Alumni Access"
    >
      <ResourceCard
        v-for="resource in alumResources"
        :key="resource.id"
        :title="`${resource.title} Resources`"
        :link="`/alumni/${resource.slug}`"
      />
    </BaseAccordion>

    <BaseAccordion
      v-if="customerDashboard?.payment_history.length"
      button-text="Payment History"
    >
      <PaymentRecordCard
        v-for="paymentRecord in customerDashboard?.payment_history"
        :key="paymentRecord.order_id"
        :payment-record="paymentRecord"
      />
    </BaseAccordion>
  </div>

  <ConfirmDeleteRegistration
    v-if="currentRegistrationId"
    :deletion-loading="deletionLoading"
    :show-delete-modal="showDeleteModal"
    :registration-id="currentRegistrationId"
    @close-delete-modal="closeDeleteModal"
    @remove-customer-registration="removeCustomerRegistration"
  />
</template>

<script setup lang="ts">
import { computed, onBeforeMount, onUnmounted, ref, watch } from 'vue'
import { getCourses } from '@js/api/courses/getCoursesByIds'
import { getAlumniResources } from '@js/api/courses/getAlumniResources'
import { getCustomerDashboard } from '@js/api/customer/getCustomerDashboard'
import { deleteCustomerRegistration } from '@js/api/customer/deleteCustomerRegistration'
import { Course } from '@js/api/types/course/Course'
import { AlumniResource } from '@js/api/types/course/AlumniResource'
import { CustomerDashboard } from '@js/api/types/customer/CustomerDashboard'
import { CustomerRegistration } from '@js/api/types/customer/CustomerRegistration'
import { useUserStore } from '@js/stores/userStore'
import { log } from '@js/utils/logger'

import BaseAccordion from '@js/components/global/BaseAccordion.vue'
import CourseCard from '@js/components/account/dashboard/CourseCard.vue'
import WebinarCard from '@js/components/account/dashboard/WebinarCard.vue'
import ResourceCard from '@js/components/account/dashboard/ResourceCard.vue'
import PaymentRecordCard from '@js/components/account/dashboard/PaymentRecordCard.vue'
import EmptyCurrentUpcomingTraining from '@js/components/account/dashboard/EmptyCurrentUpcomingTraining.vue'
import LoadingIndicator from '@js/components/checkout/partials/LoadingIndicator.vue'
import ConfirmDeleteRegistration from '@js/components/account/dashboard/ConfirmDeleteRegistration.vue'
import useAuth from '@js/composables/useAuth'

defineOptions({
  name: 'AppDashboard',
})

const { getAuthUser } = useAuth()

const user = computed(() => getAuthUser())

const userStore = useUserStore()

const isLoading = ref(false)

const showDeleteModal = ref(true)
const deletionLoading = ref(false)
const currentRegistrationId = ref<number | null>(null)

const customerDashboard = ref<CustomerDashboard | null>(null)
const alumResources = ref<AlumniResource[]>([])

const incompleteRegistrations = ref<{ registration: CustomerRegistration; course: Course }[]>([])
const enrolledRegistrations = ref<{ registration: CustomerRegistration; course: Course }[]>([])
const completedRegistrations = ref<{ registration: CustomerRegistration; course: Course }[]>([])

const POLL_INTERVAL_MS = 60_000;         // 1 minute
const POLL_MAX_DURATION_MS = 5 * 60_000; // 5 minutes

const confirmDelete = (registrationId: number): void => {
  showDeleteModal.value = true
  currentRegistrationId.value = registrationId
}

const closeDeleteModal = (): void => {
  showDeleteModal.value = false
  currentRegistrationId.value = null
}

const loadCoursesFromRegistrations = async () => {
  // Reset the arrays first to avoid duplication
  incompleteRegistrations.value = []
  enrolledRegistrations.value = []
  completedRegistrations.value = []

  if (!customerDashboard.value?.registrations?.length) {
    return
  }

  // Extract the course IDs from registrations
  const courseIds = customerDashboard.value.registrations.map(registration => registration.craft_entry_id)

  if (courseIds.length === 0) {
    return
  }

  try {
    // Fetch course data using getCourses
    const coursesData = (await getCourses({ ids: courseIds })).data

    const coursesMap = new Map(coursesData.map(course => [course.id, course]))

    // Group registrations by status
    customerDashboard.value.registrations.forEach((registration) => {
      const course = coursesMap.get(registration.craft_entry_id)

      if (!course) return

      const item = { registration, course }

      // Add to array based on status
      switch (registration.status) {
        case 'incomplete':
          incompleteRegistrations.value.push(item)
          break
        case 'enrolled':
        case 'enrollment_pending':
          enrolledRegistrations.value.push(item)
          break
        case 'completed':
          completedRegistrations.value.push(item)
          break
      }
    })
  } catch (error) {
    log({
      message: `Error loading courses: ${error}`,
      file: 'src/wcei/js/components/account/dashboard/AppDashboard.vue',
    })
  }
}

const filterResources = (resources: AlumniResource[]) => {
  return resources.filter((resource) => {
    // Check each resource against the specific criteria
    if (resource.slug === 'skin-wound-management' && customerDashboard.value?.alumni.wcc_alumni) {
      return true
    }

    if (resource.slug === 'ostomy-management-specialist' && customerDashboard.value?.alumni.oms_alumni) {
      return true
    }

    if (resource.slug === 'diabetic-wound-management' && customerDashboard.value?.alumni.dwc_alumni) {
      return true
    }

    return false
  })
}

const removeCustomerRegistration = async (registrationId: number): Promise<void> => {
  try {
    deletionLoading.value = true

    await deleteCustomerRegistration({
      registrationId: registrationId,
    })

    customerDashboard.value = await getCustomerDashboard()
    await loadCoursesFromRegistrations()

    // Update incomplete registrations count in the global user store
    userStore.decrementIncompleteRegistrationsCount()

  } catch (error) {
    log({
      message: 'Remove Customer Registration Error',
      file: 'src/wcei/js/components/account/dashboard/AppDashboard.vue',
    })
  } finally {
    deletionLoading.value = false
    showDeleteModal.value = false
    currentRegistrationId.value = null
  }
}

/** --- Polling (only pending) --- */
const pollId = ref<number | null>(null)
const pollTimeoutId = ref<number | null>(null)

const hasPendingEnrollments = computed(() =>
  !!customerDashboard.value?.registrations?.some(r => r.status === 'enrollment_pending')
)

/**
 * Poll handler to update ONLY items that were 'enrollment_pending' without
 * rebuilding arrays (prevents snap/re-render).
 */
const refreshPendingStatuses = async () => {
  try {
    const latest = await getCustomerDashboard()
    const latestRegs = latest?.registrations ?? []

    // Keep customerDashboard.registrations fresh for the hasPendingEnrollments computed
    if (customerDashboard.value) {
      customerDashboard.value = {
        ...customerDashboard.value,
        registrations: latestRegs as CustomerRegistration[],
      }
    }

    const latestById = new Map<number, CustomerRegistration>(
      latestRegs.map(r => [r.registration_id, r])
    )

    // Update only items that were pending in the "enrolledRegistrations" list
    // Mutate in place to avoid remounts
    const movedToCompleted: typeof enrolledRegistrations.value = []

    enrolledRegistrations.value.forEach((item) => {
      const id = item.registration.registration_id
      const newReg = latestById.get(id)
      if (!newReg) return

      if (item.registration.status === 'enrollment_pending') {
        // Update fields that can change
        item.registration.status = newReg.status
        item.registration.expiration_date = newReg.expiration_date
        item.registration.completed_date = newReg.completed_date
      }

      // If it transitioned to completed, queue to move
      if (newReg.status === 'completed' && item.registration.status !== 'completed') {
        item.registration.status = 'completed'
        item.registration.completed_date = newReg.completed_date
        movedToCompleted.push(item)
      }
    })

    // Move completed items without nuking lists
    if (movedToCompleted.length) {
      enrolledRegistrations.value = enrolledRegistrations.value.filter(
        (item) => item.registration.status !== 'completed'
      )
      completedRegistrations.value.push(...movedToCompleted)
    }
  } catch (error) {
    log({
      message: 'Polling: Customer Dashboard Retrieval Error',
      file: 'src/wcei/js/components/account/dashboard/AppDashboard.vue',
    })
  }
}

/**
 * Starts polling for pending enrollments every POLL_INTERVAL_MS
 */
const startPolling = () => {
  if (pollId.value) return
  pollId.value = window.setInterval(async () => {
    await refreshPendingStatuses()
    if (!hasPendingEnrollments.value) stopPolling()
  }, POLL_INTERVAL_MS)

  if (pollTimeoutId.value) {
    clearTimeout(pollTimeoutId.value)
    pollTimeoutId.value = null
  }
  pollTimeoutId.value = window.setTimeout(() => {
    stopPolling()
  }, POLL_MAX_DURATION_MS)
}

/**
 * Stops polling and clears any active intervals or timeouts
 */
const stopPolling = () => {
  if (pollId.value) {
    clearInterval(pollId.value)
    pollId.value = null
  }
  if (pollTimeoutId.value) {
    clearTimeout(pollTimeoutId.value)
    pollTimeoutId.value = null
  }
}

/**
 * Handles watching for changes on pending enrollments
 */
watch(hasPendingEnrollments, (pending) => {
  if (pending) startPolling()
  else stopPolling()
}, { immediate: true })

onBeforeMount(async () => {
  try {
    isLoading.value = true

    const [dashboardData, alumniResources] = await Promise.all([
      getCustomerDashboard(),
      getAlumniResources(),
    ])

    customerDashboard.value = dashboardData

    if (dashboardData?.alumni) {
      alumResources.value = filterResources(alumniResources.data)
    }

    await loadCoursesFromRegistrations()
  } catch (error) {
    log({
      message: 'Customer Dashboard Retrieval Error',
      file: 'src/wcei/js/components/account/dashboard/AppDashboard.vue',
    })
  }
  finally {
    isLoading.value = false
  }
})

onUnmounted(stopPolling)
</script>
